import { useEffect, useMemo, useState } from "react";
import { AutoComplete, AutoCompleteSelectEvent } from "primereact/autocomplete";
import { useHttpGets } from "../../services/httpService";
import { useGoogleMapsLoader, sanitizeHtml } from "../../utils/commonUtil";
import { ApiEndPoints } from "../../utils/endpoints";
import {
  ALLOWED_COUNTRIES,
  MetricCard,
  ScopeKey,
  StatsTabKey,
  stripCountrySuffix,
  SUBURB_COMPONENT_TYPES,
  SupplyAndDemandScope,
  SuburbInsightsResponse,
} from "../../types/seo";
import { COUNTRY } from "../../utils/constants";
import { REIDivider } from "../../components/ui";

type SuburbSuggestion = {
  label: string;
  country: string;
  suburb: string;
};

const scopeButtons: { key: ScopeKey; label: string; caption: string }[] = [
  { key: "suburb", label: "Suburb", caption: "New Farm" },
  { key: "region", label: "Region", caption: "Brisbane" },
  { key: "state", label: "State", caption: "Queensland" },
];

const statsButtons: { key: StatsTabKey; label: string }[] = [
  { key: "median", label: "Median Statistics" },
  { key: "rental", label: "Rental Statistics" },
  { key: "sales", label: "Sales Statistics" },
];

const metricCardStyles = [
  "from-amber-100 via-white to-orange-50 border-amber-200",
  "from-teal-100 via-white to-cyan-50 border-teal-200",
  "from-sky-100 via-white to-indigo-50 border-sky-200",
  "from-lime-100 via-white to-emerald-50 border-lime-200",
];

const DEFAULT_SUBURB = "ashmore";
const DEFAULT_SUBURB_LABEL = "Ashmore";

const isAllowedCountryComponent = (component: any) => {
  return component?.types?.includes("country") && ALLOWED_COUNTRIES.has(component.short_name?.toUpperCase?.() ?? "");
};

const hasSuburbComponent = (result: any) => {
  return result?.address_components?.some((component: any) =>
    component.types?.some((type: string) => SUBURB_COMPONENT_TYPES.has(type))
  );
};

const getSuburbComponentValue = (result: any) => {
  const suburbComponent = result?.address_components?.find((component: any) =>
    component.types?.some((type: string) => SUBURB_COMPONENT_TYPES.has(type))
  );

  return suburbComponent?.long_name?.trim() || suburbComponent?.short_name?.trim() || "";
};

const getAddressComponentValue = (result: any, componentType: string) => {
  const component = result?.address_components?.find((item: any) => item.types?.includes(componentType));

  return component?.long_name?.trim() || component?.short_name?.trim() || "";
};

const getSnapshotTitleValue = (result: any) => {
  const suburb = getSuburbComponentValue(result);
  const state = getAddressComponentValue(result, "administrative_area_level_1");
  const postcode = getAddressComponentValue(result, "postal_code");

  return [suburb, state, postcode].filter(Boolean).join(", ");
};

const getSuburbOnlyValue = (value: string) => {
  return stripCountrySuffix(value)
    .replace(/\s*\([^)]*\)\s*$/i, "")
    .split(",")[0]
    .trim();
};

const buildMetricCards = (scopeData: SupplyAndDemandScope): MetricCard[] => [
  { label: "Current vacancy rate", value: scopeData.currentVacancyRate },
  { label: "Rental stock available", value: scopeData.rentalStockAvailable },
  { label: "Population", value: scopeData.population },
  { label: "Rental population", value: scopeData.rentalPopulation },
];

const isSuburbResult = (result: any) => {
  if (!result?.address_components) {
    return false;
  }

  const isAllowedCountry = result.address_components.some((component: any) => isAllowedCountryComponent(component));

  return isAllowedCountry && hasSuburbComponent(result);
};

const getInvalidLocationMessage = (inputValue: string, geocodeResults: any[]) => {
  if (geocodeResults.length === 0) {
    return "Please enter a valid suburb in Australia or New Zealand.";
  }

  const hasAllowedCountryMatch = geocodeResults.some((result) =>
    result?.address_components?.some((component: any) => isAllowedCountryComponent(component))
  );

  if (!hasAllowedCountryMatch) {
    return "Please enter a valid suburb in Australia or New Zealand.";
  }

  return `\"${stripCountrySuffix(inputValue)}\" is not a suburb. Please enter a suburb name.`;
};

const SeoPage: React.FC = () => {
  const [activeScope, setActiveScope] = useState<ScopeKey>("suburb");
  const [activeStatsTab, setActiveStatsTab] = useState<StatsTabKey>("median");
  const isGoogleMapsLoaded = useGoogleMapsLoader();

  const [value, setValue] = useState<string>(DEFAULT_SUBURB_LABEL);
  const [suggestions, setSuggestions] = useState<SuburbSuggestion[]>([]);
  const [inputError, setInputError] = useState<string>("");
  const [selectedSuburb, setSelectedSuburb] = useState<SuburbSuggestion | null>(null);
  const [suburbInsights, setSuburbInsights] = useState<SuburbInsightsResponse | null>(null);
  const [isLoadingSuburbInsights, setIsLoadingSuburbInsights] = useState(false);
  const [suburbInsightsError, setSuburbInsightsError] = useState("");
  const [snapshotTitle, setSnapshotTitle] = useState("");

  const clearInputWithError = (message: string) => {
    setValue("");
    setSuggestions([]);
    setSelectedSuburb(null);
    setSuburbInsights(null);
    setSuburbInsightsError("");
    setInputError(message);
    setSnapshotTitle("");
  };

  const getSuburbQueryValue = (suburbValue: string) => getSuburbOnlyValue(suburbValue).toLowerCase();

  const getDefaultSuburbLabel = async () => {
    const result = await useHttpGets(`${ApiEndPoints.address}/${COUNTRY.AU}/${DEFAULT_SUBURB}`);

    if (result.status !== 200 || !Array.isArray(result.data)) {
      return DEFAULT_SUBURB_LABEL;
    }

    const defaultMatch = (result.data as any[]).find(
      (item: any) => getSuburbQueryValue(item.LocalityLabel) === DEFAULT_SUBURB
    );

    return defaultMatch?.LocalityLabel || DEFAULT_SUBURB_LABEL;
  };

  const fetchSuburbInsights = async (suburbValue: string) => {
    const suburb = getSuburbQueryValue(suburbValue);

    if (!suburb) {
      setSuburbInsights(null);
      return;
    }

    setIsLoadingSuburbInsights(true);
    setSuburbInsightsError("");

    const result = await useHttpGets(`${ApiEndPoints.seo}`, { suburb });
    const responseData = result.data as Partial<SuburbInsightsResponse> | null;

    if (result.status === 200 && responseData?.isValid) {
      setSuburbInsights(responseData as SuburbInsightsResponse);
      if (suburb === DEFAULT_SUBURB) {
        await validateSuburbWithGoogle(suburbValue);
      }
    } else {
      setSuburbInsights(null);
      setSuburbInsightsError(responseData?.validationError || "We could not load suburb insights. Please try again.");
    }

    setIsLoadingSuburbInsights(false);
  };

  useEffect(() => {
    void (async () => {
      const defaultLabel = await getDefaultSuburbLabel();
      setValue(defaultLabel);
      await fetchSuburbInsights(defaultLabel);
    })();
  }, []);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !suburbInsights || snapshotTitle || getSuburbQueryValue(value) !== DEFAULT_SUBURB) {
      return;
    }

    void validateSuburbWithGoogle(value);
  }, [isGoogleMapsLoaded, snapshotTitle, suburbInsights, value]);

  const getGeocodeResults = async (inputValue: string) => {
    const googleMaps = (window as any).google?.maps;

    if (!isGoogleMapsLoaded || !googleMaps?.Geocoder) {
      return null;
    }

    const geocoder = new googleMaps.Geocoder();
    return new Promise<any[]>((resolve, reject) => {
      geocoder.geocode({ address: stripCountrySuffix(inputValue) }, (results: any[] | null, status: string) => {
        if (status === "OK" && results) {
          resolve(results);
          return;
        }

        if (status === "ZERO_RESULTS") {
          resolve([]);
          return;
        }

        reject(new Error(status));
      });
    });
  };

  const validateSuburbWithGoogle = async (inputValue: string) => {
    const geocodeResults = await getGeocodeResults(inputValue);

    if (!geocodeResults) {
      return {
        isValid: false,
        suburb: "",
        message: "Location validation unavailable. Please select a suburb from the suggestions list.",
      };
    }

    const matchedResult = geocodeResults.find((result: any) => isSuburbResult(result));
    const suburb = matchedResult ? getSuburbComponentValue(matchedResult) : "";
    const isValid = Boolean(matchedResult && suburb);
    setSnapshotTitle(matchedResult ? getSnapshotTitleValue(matchedResult) : "");
    return {
      isValid,
      suburb,
      message: isValid ? "" : getInvalidLocationMessage(inputValue, geocodeResults),
    };
  };

  const searchItems = async (event: { query: string }) => {
    const query = event.query?.trim();
    if (!query || query.length < 2) {
      setSuggestions([]);
      setInputError("");
      return;
    }

    try {
      const [auResult, nzResult] = await Promise.all([
        useHttpGets(`${ApiEndPoints.address}/${COUNTRY.AU}/${query}`),
        useHttpGets(`${ApiEndPoints.address}/${COUNTRY.NZ}/${query}`),
      ]);

      const auSuggestions: SuburbSuggestion[] =
        auResult.status === 200 && Array.isArray(auResult.data)
          ? (auResult.data as any[]).map((item: any) => ({
              label: item.LocalityLabel,
              country: COUNTRY.AU,
              suburb: getSuburbOnlyValue(item.LocalityLabel),
            }))
          : [];

      const nzSuggestions: SuburbSuggestion[] =
        nzResult.status === 200 && Array.isArray(nzResult.data)
          ? (nzResult.data as any[]).map((item: any) => ({
              label: `${item.LocalityLabel} (NZ)`,
              country: COUNTRY.NZ,
              suburb: getSuburbOnlyValue(item.LocalityLabel),
            }))
          : [];

      const combined = [...auSuggestions, ...nzSuggestions];
      setSuggestions(combined);
      setInputError(combined.length === 0 ? "No matching suburb found. Please try a different name." : "");
    } catch {
      setSuggestions([]);
      setInputError("Something went wrong. Please try again.");
    }
  };

  const handleSelect = async (item: SuburbSuggestion) => {
    setValue(item.label);

    const validationResult = await validateSuburbWithGoogle(item.label);

    if (!validationResult.isValid) {
      clearInputWithError(validationResult.message);
      return;
    }

    const validatedSuburb = item.suburb;

    setSelectedSuburb({
      ...item,
      suburb: validatedSuburb,
    });
    setInputError("");
    await fetchSuburbInsights(validatedSuburb);
  };

  const handleChange = (nextValue: string) => {
    setValue(nextValue);

    if (!selectedSuburb || selectedSuburb.label !== nextValue) {
      setSelectedSuburb(null);
      setSuburbInsights(null);
      setSnapshotTitle("");
    }

    if (inputError) {
      setInputError("");
    }

    if (suburbInsightsError) {
      setSuburbInsightsError("");
    }
  };

  const pageTitle = "Suburb Property Insights";
  const suburbDescriptionHtml = suburbInsights?.suburbDescription?.html
    ? sanitizeHtml(suburbInsights.suburbDescription.html)
    : undefined;

  const scopeButtonData = useMemo(
    () =>
      scopeButtons.map((button) => ({
        ...button,
        caption: suburbInsights?.supplyAndDemand?.[button.key]?.name || "",
      })),
    [suburbInsights]
  );

  const activeScopeData = useMemo(() => {
    const scopeData = suburbInsights?.supplyAndDemand?.[activeScope];

    if (!scopeData) {
      return null;
    }

    return {
      title: scopeData.title,
      subtitle: scopeData.subtitle,
      cards: buildMetricCards(scopeData),
    };
  }, [activeScope, suburbInsights]);

  const activeStatsData = suburbInsights?.statistics?.[activeStatsTab];
  const hasSuburbInsights = Boolean(suburbInsights);

  const tableColumns = useMemo(() => {
    return activeStatsTab === "sales" ? ["Metric", "House", "Units"] : ["Metric", "House", "Townhouses", "Units"];
  }, [activeStatsTab]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="w-full">
        <section className="bg-white mb-5">
          <div className="mx-auto max-w-7xl px-4 pb-3 pt-12 text-center sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black mb-2 mt-4 md:mb-3 md:mt-3 tracking-wide">
              {pageTitle}
            </h1>
            <div className="mx-auto max-w-2xl text-center">
              <p className="mb-2 leading-6 text-slate-500 font-medium">
                Enter a suburb name to see a clear snapshot of the local property market, including key insights and
                trends to help you understand how the area is performing.
              </p>
              <div className="rounded-2xl border border-black bg-gradient-to-r from-amber-50 via-white to-teal-50 p-4 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.45)] ring-1 ring-black/15 sm:p-5">
                <AutoComplete
                  inputId="seo-txt-suburb"
                  data-testid="seo-txt-suburb"
                  value={value}
                  suggestions={suggestions}
                  completeMethod={searchItems}
                  field="label"
                  forceSelection
                  placeholder="Please enter a suburb (Australia or New Zealand)"
                  className={`w-full ${inputError ? "p-invalid" : ""}`}
                  inputClassName="w-full rounded-xl border-2 border-black bg-white px-4 py-3 text-base font-medium text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-cyan-600 focus:ring-4 focus:ring-cyan-100"
                  onChange={(e: any) => {
                    const nextValue = typeof e.value === "string" ? e.value : (e.value?.label ?? "");
                    handleChange(nextValue);
                  }}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    void handleSelect(e.value as SuburbSuggestion);
                  }}
                  minLength={2}
                  disabled={isLoadingSuburbInsights}
                />
                <p className="mt-2 text-left text-xs font-medium text-slate-500">
                  Suggestions appear after 2 characters. Selection is required.
                </p>
                {inputError && <small className="p-error mt-1 block text-left">{inputError}</small>}
                {!inputError && suburbInsightsError && (
                  <small className="p-error mt-1 block text-left">{suburbInsightsError}</small>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-100">
          <article className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:px-8 lg:px-8">
            <h1 className="text-center">{snapshotTitle}</h1>
            {suburbDescriptionHtml ? (
              <div
                className="prose prose-slate mt-5 max-w-none text-sm leading-6"
                dangerouslySetInnerHTML={{ __html: suburbDescriptionHtml }}
              />
            ) : (
              <div className="mt-5 leading-6 text-slate-500 text-center">
                {isLoadingSuburbInsights ? "Loading suburb description..." : "No valid suburb selected."}
              </div>
            )}
          </article>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 bg-gray-100 px-6 pt-5 pb-4 md:px-8">
                <div className="mt-3 grid grid-cols-3 overflow-hidden rounded-xl border border-gray-100 bg-gray-100">
                  {scopeButtonData.map((button) => {
                    const isActive = button.key === activeScope;
                    return (
                      <button
                        key={button.key}
                        id={`seo-btn-scope-${button.key}`}
                        data-testid={`seo-btn-scope-${button.key}`}
                        type="button"
                        onClick={() => setActiveScope(button.key)}
                        className={`border-r py-3 text-center transition last:border-r-2 last:border-black  ${
                          isActive
                            ? "border-r-[#238EA0] bg-[#238EA0] text-white"
                            : "border-r-gray-100 bg-gray-100 text-slate-600 hover:bg-gray-50"
                        }`}
                        disabled={!hasSuburbInsights}
                      >
                        <div className="text-xs font-bold uppercase tracking-[0.18em]">{button.label}</div>
                        {button.caption && <div className="mt-0.5 text-sm font-semibold">{button.caption}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
              <REIDivider className="p-0 m-0 bg-gray-100"></REIDivider>

              <div className="px-6 py-6 md:px-8 bg-gray-100">
                <div className="rounded-xl border border-gray-100  p-6 md:p-7">
                  {activeScopeData ? (
                    <>
                      <h2 className="text-xl font-semibold text-slate-900">{activeScopeData.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-500">{activeScopeData.subtitle}</p>
                      <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4 ">
                        {activeScopeData.cards.map((card, index) => (
                          <div
                            key={card.label}
                            className={`rounded-xl border bg-gradient-to-br p-5 ${metricCardStyles[index % metricCardStyles.length]}`}
                          >
                            <div className="flex h-full items-center justify-center text-center">
                              <div className="flex aspect-square w-full max-w-[210px] flex-col items-center justify-center rounded-full border border-slate-400/80 bg-slate-400/75 px-5 shadow-sm backdrop-blur">
                                <p className="text-sm font-medium leading-5 text-slate-600">{card.label}</p>
                                <p className="mt-4 text-3xl font-black text-slate-900">{card.value}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm leading-6 text-slate-500">
                      {isLoadingSuburbInsights
                        ? "Loading supply and demand data..."
                        : "No supply and demand data available."}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-100">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 bg-white px-6 pt-5 pb-4 md:px-8">
                <div className=" grid grid-cols-3 overflow-hidden rounded-xl border border-gray-100 bg-white">
                  {statsButtons.map((button) => {
                    const isActive = button.key === activeStatsTab;
                    return (
                      <button
                        key={button.key}
                        id={`seo-btn-stats-${button.key}`}
                        data-testid={`seo-btn-stats-${button.key}`}
                        type="button"
                        onClick={() => setActiveStatsTab(button.key)}
                        className={`border-r py-3 text-center text-sm font-semibold transition last:border-r-2 last:border-black ${
                          isActive
                            ? "border-r-[#238EA0] bg-[#238EA0] text-white"
                            : "border-r-slate-100 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                        disabled={!hasSuburbInsights}
                      >
                        {button.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <REIDivider className="p-0 m-0"></REIDivider>
              <div className="px-6 py-6 md:px-8">
                {activeStatsData ? (
                  <>
                    <h2 className="text-xl font-semibold text-slate-900">{activeStatsData.title}</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-500">{activeStatsData.description}</p>

                    <div className="mt-5 overflow-hidden rounded-xl border border-slate-100">
                      <div
                        className="grid bg-slate-900 text-sm font-bold uppercase tracking-[0.16em] text-white"
                        style={{
                          gridTemplateColumns:
                            activeStatsTab === "sales" ? "1.5fr 0.8fr 0.8fr" : "1.5fr 0.8fr 0.8fr 0.8fr",
                        }}
                      >
                        {tableColumns.map((column) => (
                          <div key={column} className="px-4 py-4">
                            {column}
                          </div>
                        ))}
                      </div>

                      {activeStatsData.rows.map((row, index) => (
                        <div
                          key={row.label}
                          className={`grid text-sm text-slate-700 ${index % 2 === 0 ? "bg-slate-50" : "bg-white"}`}
                          style={{
                            gridTemplateColumns:
                              activeStatsTab === "sales" ? "1.5fr 0.8fr 0.8fr" : "1.5fr 0.8fr 0.8fr 0.8fr",
                          }}
                        >
                          <div className="border-t border-slate-100 px-4 py-4 font-semibold">{row.label}</div>
                          <div className="border-t border-slate-100 px-4 py-4">{row.house}</div>
                          {activeStatsTab !== "sales" && (
                            <div className="border-t border-slate-100 px-4 py-4">{row.townhouses}</div>
                          )}
                          <div className="border-t border-slate-100 px-4 py-4">{row.units}</div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-sm leading-6 text-slate-500">
                    {isLoadingSuburbInsights ? "Loading statistics..." : "No statistics available."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SeoPage;
